import type { Person, Server, ServerMember, Job, Shift, Connection, Agreement, Approval, Rule, LogEntry, Channel, Role, ServerChannel, VoiceChannel } from './types';

export interface AppState {
  servers: Server[];
  people: Person[];
  serverMembers: ServerMember[];
  jobs: Job[];
  shifts: Shift[];
  connections: Connection[];
  agreements: Agreement[];
  approvals: Approval[];
  roles: Role[];
  rules: Rule[];
  logs: LogEntry[];
  /** Text channels for the current server, fetched from /api/channels */
  channels: ServerChannel[];
  /** Voice channels across all servers, fetched per-server from /api/voice-channels */
  voiceChannels: VoiceChannel[];
  /** LiveKit room the user is currently connected to (null = not in voice) */
  activeVoiceChannelId: string | null;
  /**
   * Channel names (for the current server) the current user may read.
   * null = not yet loaded, show everything (permissive default).
   * Set, empty or otherwise = only show these text channel names in the sidebar.
   */
  visibleChannelNames: Set<string> | null;
  currentServer: string | null;
  currentServerId: string | null;
  currentChannel: Channel;
  theme: 'dark' | 'light';
  cellAvailable: boolean;
  ownerId: string | null;
  /** Feature flags for the authed user — fetched once per session from /api/feature-flags/me. */
  flags: Record<string, boolean>;
  /** person_id -> hex color, for the current server. Populated from
   *  /api/server_members/role_colors. Empty = none loaded yet. */
  roleColors: Record<string, string>;
}

export const state: AppState = {
  servers: [],
  people: [],
  serverMembers: [],
  jobs: [],
  shifts: [],
  connections: [],
  agreements: [],
  approvals: [],
  roles: [],
  rules: [],
  logs: [],
  channels: [],
  voiceChannels: [],
  activeVoiceChannelId: null,
  visibleChannelNames: null,
  currentServer: null,
  currentServerId: null,
  currentChannel: 'general',
  theme: 'dark',
  cellAvailable: false,
  ownerId: null,
  flags: {},
  roleColors: {},
};

export function getPersonName(id: string): string {
  return state.people.find(p => p.id === id)?.name ?? 'Unknown';
}

export function getServerName(id: string): string {
  return state.servers.find(s => s.id === id)?.name ?? 'Unknown';
}

export function getCurrentServerPeople(): Person[] {
  const sid = state.currentServerId ?? state.currentServer;
  if (!sid) return state.people;
  const memberIds = state.serverMembers
    .filter(m => m.server_id === sid)
    .map(m => m.person_id);
  return state.people.filter(p => memberIds.includes(p.id));
}
